import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import type {
  MajorGamesBenchmarkQualifier,
  SingaporeStats,
} from '../types';

const { Text, Title } = Typography;

interface SingaporeSummaryProps {
  data: SingaporeStats | null;
  benchmarkLoading?: boolean;
}

type GenderFilter = 'all' | 'W' | 'M';

interface StandardsDisplayRow {
  row_key: string;
  event_name: string;
  women_five?: string;
  women_three_point_five?: string;
  women_two?: string;
  women_sixth?: string;
  men_sixth?: string;
  men_two?: string;
  men_three_point_five?: string;
  men_five?: string;
}

const tierColors: Record<MajorGamesBenchmarkQualifier['tier_key'], string> = {
  sixth_position: 'gold',
  two_percent: 'green',
  three_point_five_percent: 'blue',
  five_percent: 'volcano',
};

const formatDate = (value: string) => (value ? new Date(value).toLocaleDateString() : '—');

const qualifierColumns = [
  {
    title: 'Athlete',
    dataIndex: 'full_name',
    key: 'full_name',
    ellipsis: true,
    render: (name: string) => <Text strong>{name}</Text>,
  },
  {
    title: 'Gender',
    dataIndex: 'gender',
    key: 'gender',
    width: 100,
    render: (gender: string) => <Tag>{gender === 'W' ? 'Women' : 'Men'}</Tag>,
  },
  {
    title: 'Event',
    dataIndex: 'event_name',
    key: 'event_name',
    width: 150,
  },
  {
    title: 'Mark',
    dataIndex: 'display_mark',
    key: 'display_mark',
    width: 95,
    render: (mark: string, row: MajorGamesBenchmarkQualifier) => mark || row.mark,
  },
  {
    title: 'Tier',
    dataIndex: 'tier_label',
    key: 'tier_label',
    width: 120,
    render: (label: string, row: MajorGamesBenchmarkQualifier) => (
      <Tag color={tierColors[row.tier_key]}>{label}</Tag>
    ),
  },
  {
    title: 'Competition',
    dataIndex: 'competition_name',
    key: 'competition_name',
    ellipsis: true,
  },
  {
    title: 'Date',
    dataIndex: 'competition_start_date',
    key: 'competition_start_date',
    width: 120,
    render: formatDate,
  },
];

const SingaporeSummary: React.FC<SingaporeSummaryProps> = ({
  data,
  benchmarkLoading = false,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [genderFilter, setGenderFilter] = useState<GenderFilter>('all');
  const [eventFilter, setEventFilter] = useState('all');
  const benchmarks = data?.major_games_benchmarks;

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const eventOptions = useMemo(() => {
    const events = new Set<string>();
    benchmarks?.standards.forEach((row) => {
      if (row.gender === 'M' || row.gender === 'W') events.add(row.event_name);
    });
    benchmarks?.women.forEach((row) => events.add(row.event_name));
    benchmarks?.men.forEach((row) => events.add(row.event_name));
    return Array.from(events).sort().map((eventName) => ({
      value: eventName,
      label: eventName,
    }));
  }, [benchmarks]);

  const standards = useMemo<StandardsDisplayRow[]>(() => {
    const rows = new Map<string, StandardsDisplayRow>();
    (benchmarks?.standards ?? [])
      .filter((row) => row.gender === 'M' || row.gender === 'W')
      .filter((row) => genderFilter === 'all' || row.gender === genderFilter)
      .filter((row) => eventFilter === 'all' || row.event_name === eventFilter)
      .forEach((row) => {
        const existing = rows.get(row.event_name) ?? {
          row_key: row.event_name,
          event_name: row.event_name,
        };
        if (row.gender === 'W') {
          existing.women_five = row.five_percent;
          existing.women_three_point_five = row.three_point_five_percent;
          existing.women_two = row.two_percent;
          existing.women_sixth = row.sixth_position;
        } else {
          existing.men_sixth = row.sixth_position;
          existing.men_two = row.two_percent;
          existing.men_three_point_five = row.three_point_five_percent;
          existing.men_five = row.five_percent;
        }
        rows.set(row.event_name, existing);
      });
    return Array.from(rows.values());
  }, [benchmarks?.standards, eventFilter, genderFilter]);

  const filteredWomen = useMemo(
    () => (genderFilter === 'M'
      ? []
      : (benchmarks?.women ?? []).filter((row) => eventFilter === 'all' || row.event_name === eventFilter)),
    [benchmarks?.women, eventFilter, genderFilter]
  );

  const filteredMen = useMemo(
    () => (genderFilter === 'W'
      ? []
      : (benchmarks?.men ?? []).filter((row) => eventFilter === 'all' || row.event_name === eventFilter)),
    [benchmarks?.men, eventFilter, genderFilter]
  );

  const filteredQualifiers = useMemo(
    () => [...filteredWomen, ...filteredMen],
    [filteredMen, filteredWomen]
  );

  if (benchmarkLoading && !benchmarks) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" tip="Loading benchmark standards..." />
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '0 8px' : 0 }}>
      <Title level={4} style={{ marginBottom: 4, fontSize: isMobile ? 16 : undefined }}>
        Major Games Benchmarking
      </Title>
      <Text type="secondary">
        Asian Athletics Games 2026 qualifying period:{' '}
        {benchmarks
          ? `${formatDate(benchmarks.qualifying_period.start)} to ${formatDate(benchmarks.qualifying_period.end)}`
          : '19 Jun 2025 to 15 Jun 2026'}
      </Text>

      <Space wrap style={{ marginTop: 16 }}>
        <Select
          aria-label="Filter by gender"
          value={genderFilter}
          style={{ width: 180 }}
          onChange={setGenderFilter}
          options={[
            { value: 'all', label: 'All genders' },
            { value: 'W', label: 'Women' },
            { value: 'M', label: 'Men' },
          ]}
        />
        <Select
          aria-label="Filter by event"
          showSearch
          value={eventFilter}
          style={{ width: 240 }}
          onChange={setEventFilter}
          optionFilterProp="label"
          options={[{ value: 'all', label: 'All events' }, ...eventOptions]}
        />
      </Space>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="Asian Athletics Games 2026 standards" size={isMobile ? 'small' : undefined}>
            <Table
              dataSource={standards}
              rowKey="row_key"
              size={isMobile ? 'small' : 'middle'}
              scroll={{ x: 1040 }}
              pagination={false}
              columns={[
                ...(genderFilter === 'M' ? [] : [
                  { title: "Women's 5%", dataIndex: 'women_five', key: 'women_five' },
                  { title: "Women's 3.5%", dataIndex: 'women_three_point_five', key: 'women_three_point_five' },
                  { title: "Women's 2%", dataIndex: 'women_two', key: 'women_two' },
                  { title: "Women's 6th Place", dataIndex: 'women_sixth', key: 'women_sixth' },
                ]),
                {
                  title: 'Event',
                  dataIndex: 'event_name',
                  key: 'event_name',
                  width: 180,
                  render: (name: string) => <Text strong>{name}</Text>,
                },
                ...(genderFilter === 'W' ? [] : [
                  { title: "Men's 6th Place", dataIndex: 'men_sixth', key: 'men_sixth' },
                  { title: "Men's 2%", dataIndex: 'men_two', key: 'men_two' },
                  { title: "Men's 3.5%", dataIndex: 'men_three_point_five', key: 'men_three_point_five' },
                  { title: "Men's 5%", dataIndex: 'men_five', key: 'men_five' },
                ]),
              ]}
            />
          </Card>
        </Col>

        <Col span={24}>
          <Card title="Athletes who met the marks" size={isMobile ? 'small' : undefined}>
            <Table
              dataSource={filteredQualifiers}
              rowKey={(row) => `${row.gender}-${row.full_name}-${row.discipline_code}-${row.tier_key}`}
              size={isMobile ? 'small' : 'middle'}
              scroll={{ x: 1080 }}
              pagination={{ pageSize: 10, showSizeChanger: false }}
              columns={qualifierColumns}
              locale={{ emptyText: 'No athletes have met the benchmark marks in the qualifying period.' }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default SingaporeSummary;
